
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://ejxokqjbhhxrwwvwkojd.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqeG9rcWpiaGh4cnd3dndrb2pkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY1OTAxNDcsImV4cCI6MjA2MjE2NjE0N30.UN6sO9vPaAy_j3nSvbTe98Iki7s1OcZ0esgbUbt2fZs";

// Maximum retry attempts for network errors
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second delay between retries, will increase with backoff

// Helper function to implement retry logic
const withRetry = async <T>(fn: () => Promise<T>, retries = MAX_RETRIES, delay = RETRY_DELAY): Promise<T> => {
  try {
    return await fn();
  } catch (error: any) {
    console.error('Error in Supabase operation:', error);
    
    if (
      retries > 0 && 
      (error.message?.includes('network') || 
       error.message?.includes('connection') ||
       error.name === 'TypeError' ||
       !navigator.onLine)
    ) {
      console.log(`Retrying operation, ${retries} attempts left`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay * 1.5);
    }
    throw error;
  }
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false, // Disable automatic URL detection
    flowType: 'pkce' // Use PKCE for better security
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_PUBLISHABLE_KEY,
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

// Enhanced helper for database operations with retry logic
export const enhancedQuery = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>
): Promise<T> => {
  return withRetry(async () => {
    const { data, error } = await operation();
    if (error) throw error;
    if (!data) throw new Error("No data returned");
    return data;
  });
};

// Helper to detect network issues
export const checkNetworkConnection = () => {
  return {
    online: navigator.onLine,
    connectionType: (navigator as any).connection ? 
      (navigator as any).connection.effectiveType : 
      'unknown'
  };
};

// Set up a listener to handle connectivity changes
if (typeof window !== 'undefined') {
  const updateSupabaseStatus = () => {
    if (navigator.onLine) {
      console.log('Back online, resuming Supabase operations');
    } else {
      console.log('Offline, pausing Supabase operations');
    }
  };

  window.addEventListener('online', updateSupabaseStatus);
  window.addEventListener('offline', updateSupabaseStatus);
}
